import React, { VFC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Container,
  Row,
  Col,
  useScreenClass,
} from 'react-grid-system';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useSettings } from '../contexts/settingsContext';
import Spacing from '../components/atoms/Spacing';
import Typography from '../components/atoms/Typography';
import PrimaryButton from '../components/molecules/PrimaryButton';
import SvgIcon from '../components/atoms/SvgIcon';
import { useReturnContext } from '../contexts/returnContext';
import FileInputComponent from '../components/molecules/FileInput';
import { tUpdateAndAddToReturnArgs } from '../contexts/zod/types';
import { theme } from '../utils/theme';

interface Props {
  radius?: string;
}

interface Ivalues {
  file: File | null;
}

const TEMPLATE_FIELDS = [
  { key: 'ordernumber', label: 'Order number', required: true },
  { key: 'customernumber', label: 'Customer number', required: true },
  { key: 'sku', label: 'SKU', required: true },
  { key: 'ean', label: 'EAN', required: true },
  { key: 'quantity', label: 'Quantity', required: true },
  { key: 'reason', label: 'Reason', required: true },
  { key: 'comment', label: 'Comment', required: false },
];

const EXAMPLE_VALUES: Record<string, string> = {
  ordernumber: 'ORD123',
  customernumber: 'CUST456',
  sku: 'SKU789',
  ean: '',
  quantity: '1',
  reason: '103',
  comment: 'Example comment',
};

const requiredFields = TEMPLATE_FIELDS.filter((f) => f.required);
const optionalFields = TEMPLATE_FIELDS.filter((f) => !f.required);

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SurroundingBorder = styled(Container) <Props>`
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const ErrorSpan = styled.span`
    color: red;
    display: block;
    font-size: 1.2rem;
  `;

const TemplateAnchor = styled.div`
  position: relative;
  display: inline-block;
`;

const TriggerButton = styled.button<{ radius?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  min-height: 5rem;
  padding: 0 2rem;
  color: ${theme.colors.grey[90]};
  background-color: ${theme.colors.grey[0]};
  border: 1px solid ${theme.colors.grey[40]};
  border-radius: ${({ radius }) => radius};
  cursor: pointer;
  transition: background-color 150ms;
  &:hover {
    background-color: ${theme.colors.grey[10]};
  }
`;

const CustomizePanel = styled.div`
  position: fixed;
  bottom: 12px;
  right: 12px;
  z-index: 10;
  width: 38rem;
  max-height: 72vh;
  overflow-y: auto;
  padding: 2.4rem;
  background-color: ${theme.colors.grey[0]};
  border: 1px solid ${theme.colors.grey[30]};
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
  text-align: left;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.6rem;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
`;

const PopoverDivider = styled.hr`
  border: none;
  border-top: 1px solid ${theme.colors.grey[30]};
  margin: 1.6rem 0;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  margin-bottom: 0.4rem;
`;

const SectionTitle = styled(Typography)`
  letter-spacing: 0.01em;
`;

const Badge = styled.span`
  flex-shrink: 0;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  background-color: ${theme.colors.grey[20]};
  color: ${theme.colors.grey[70]};
  font-size: 1.2rem;
  white-space: nowrap;
`;

const FieldRow = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.2rem 0;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  & + & {
    border-top: 1px solid ${theme.colors.grey[20]};
  }
`;

const FieldText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Switch = styled.div<{ on: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 4rem;
  height: 2.2rem;
  border-radius: 1.1rem;
  background-color: ${({ on }) => (on ? theme.colors.green[80] : theme.colors.grey[50])};
  transition: background-color 150ms;
`;

const SwitchKnob = styled.span<{ on: boolean }>`
  position: absolute;
  top: 0.2rem;
  left: ${({ on }) => (on ? '2rem' : '0.2rem')};
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  background-color: ${theme.colors.grey[0]};
  transition: left 150ms;
`;

const GenerateButton = styled.button<{ background: string; radius?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 4.4rem;
  margin-top: 0.8rem;
  padding: 0 1.6rem;
  background-color: ${({ background }) => background};
  border: none;
  border-radius: ${({ radius }) => radius};
  cursor: pointer;
  transition: opacity 150ms;
  &:hover {
    opacity: 0.9;
  }
`;

const ReturnByFilePage: VFC = () => {
  const { cornerRadius, fontFamily, interactionColor } = useSettings();
  const { validateFile, addToReturns } = useReturnContext();
  const screenClass = useScreenClass();
  const [uploading, setUploading] = useState(false);
  const [theFile, setFile] = React.useState<File | undefined>(undefined);
  const [formErrors, setFormErrors] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [selectedOptional, setSelectedOptional] = useState<Record<string, boolean>>({
    comment: false,
  });
  const [includeExample, setIncludeExample] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  const shouldScale = ['lg', 'xl', 'xxl'].includes(screenClass);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (templateRef.current && !templateRef.current.contains(e.target as Node)) {
        setTemplateOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const toggleOptional = (key: string) =>
    setSelectedOptional((prev) => ({ ...prev, [key]: !prev[key] }));

  const downloadTemplate = () => {
    const headers = TEMPLATE_FIELDS
      .filter((f) => f.required || selectedOptional[f.key])
      .map((f) => f.key);

    const rows = [headers.join(';')];
    if (includeExample) {
      rows.push(headers.map((h) => EXAMPLE_VALUES[h]).join(';'));
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'returns-template.csv';
    link.click();
    URL.revokeObjectURL(url);
    setTemplateOpen(false);
  };

  const onSubmit = async (values: Ivalues, resetForm: any) => {
    setUploading(true);
    if (values.file) {
      const response = await validateFile(values.file);
      if (response && response?.errors) {
        setFormErrors(response.errors);
      } else if (response && response?.data?.lines) {
        response.data.lines.forEach((line: tUpdateAndAddToReturnArgs) => {
          addToReturns(line, true);
          setFormErrors([]);
        });
      }
      setFile(undefined);
      resetForm();
    }
    setUploading(false);
  };

  const onChange = async (file: File, setFieldValue: any) => {
    setFile(file);
    setFieldValue('file', file);
  };

  const validationSchema = Yup.object().shape({
    file: Yup.mixed().required('File is required'),
  });

  return (
    <SurroundingBorder fluid radius={cornerRadius}>
      <Col style={{ backgroundColor: 'white' }}>
        <Spacing spacing={shouldScale ? 6 : 3} />
        <Row nogutter justify="center">
          <Typography variant="h3" translateKey="import_from_csv" />
        </Row>
        <Row nogutter justify="center">
          <Formik
            initialValues={{ file: null }}
            validationSchema={validationSchema}
            onSubmit={async (values: Ivalues, { resetForm }) => {
              onSubmit(values, resetForm);
            }}
          >
            {({ setFieldValue }) => (
              <StyledForm>
                <Spacing spacing={shouldScale ? 4 : 2} />
                <Field
                  id="file"
                  name="file"
                  type="file"
                  accept=".csv"
                  icon="upload"
                  updateFile={(e) => onChange(e, setFieldValue)}
                  file={theFile}
                  component={FileInputComponent}
                />
                <Spacing spacing={shouldScale ? 4 : 2} />
                <PrimaryButton
                  disabled={uploading}
                  loading={uploading}
                  type="submit"
                  color="primary"
                  translateKey="upload_file"
                  icon="upload"
                  iconSize="large"
                />
                <Spacing spacing={shouldScale ? 6 : 3} />
                {formErrors &&
                  formErrors.map((error) =>
                  (
                    <ErrorSpan
                      style={{ fontFamily }}
                      key={error.code}
                    >
                      {error.message}
                    </ErrorSpan>
                  ),
                  )}
                <Spacing spacing={shouldScale ? 6 : 3} />
              </StyledForm>
            )}
          </Formik>
        </Row>
      </Col>
      <Col>
        <Spacing spacing={4} />
        <Row nogutter justify="center">
          <TemplateAnchor ref={templateRef}>
            <TriggerButton
              type="button"
              radius={cornerRadius}
              onClick={() => setTemplateOpen((open) => !open)}
            >
              <SvgIcon icon="file-plus" color={theme.colors.grey[90]} iconSize="large" />
              <Typography variant="button" color={theme.colors.grey[90]}>
                Generate file template
              </Typography>
            </TriggerButton>

            {templateOpen && (
              <CustomizePanel>
                <PanelHeader>
                  <div>
                    <Typography variant="h4">Generate file template</Typography>
                    <Spacing spacing={1} />
                    <Typography variant="body2" color={theme.colors.grey[60]}>
                      Choose the data fields to include in your template.
                    </Typography>
                  </div>
                  <CloseButton type="button" onClick={() => setTemplateOpen(false)}>
                    <SvgIcon icon="cross" color={theme.colors.grey[60]} iconSize="small" />
                  </CloseButton>
                </PanelHeader>

                <PopoverDivider />

                <SectionHeader>
                  <SectionTitle variant="body3" color={theme.colors.grey[70]}>
                    Required fields
                  </SectionTitle>
                  <Badge>
                    <Typography variant="body3" color={theme.colors.grey[70]} style={{ fontSize: '1rem' }}>
                      Always included
                    </Typography>
                  </Badge>
                </SectionHeader>
                {requiredFields.map((field) => (
                  <FieldRow key={field.key}>
                    <FieldText>
                      <Typography variant="body1-semi-bold">{field.label}</Typography>
                    </FieldText>
                    <SvgIcon icon="lock" color={theme.colors.grey[60]} iconSize="small" />
                  </FieldRow>
                ))}

                <PopoverDivider />

                <SectionHeader>
                  <SectionTitle variant="body3" color={theme.colors.grey[70]}>
                    Optional fields
                  </SectionTitle>
                </SectionHeader>
                {optionalFields.map((field) => (
                  <FieldRow
                    key={field.key}
                    clickable
                    onClick={() => toggleOptional(field.key)}
                  >
                    <FieldText>
                      <Typography variant="body1-semi-bold">{field.label}</Typography>
                    </FieldText>
                    <Switch on={!!selectedOptional[field.key]}>
                      <SwitchKnob on={!!selectedOptional[field.key]} />
                    </Switch>
                  </FieldRow>
                ))}

                <PopoverDivider />

                <SectionHeader>
                  <SectionTitle variant="body3" color={theme.colors.grey[70]}>
                    Other options
                  </SectionTitle>
                </SectionHeader>
                <FieldRow clickable onClick={() => setIncludeExample(!includeExample)}>
                  <FieldText>
                    <Typography variant="body1-semi-bold">Include sample data</Typography>
                  </FieldText>
                  <Switch on={includeExample}>
                    <SwitchKnob on={includeExample} />
                  </Switch>
                </FieldRow>

                <PopoverDivider />

                <GenerateButton
                  type="button"
                  background={interactionColor}
                  radius={cornerRadius}
                  onClick={downloadTemplate}
                >
                  <Typography variant="button" color={theme.colors.grey[0]}>
                    Download template
                  </Typography>
                </GenerateButton>
              </CustomizePanel>
            )}
          </TemplateAnchor>
        </Row>
        <Spacing spacing={4} />
      </Col>
    </SurroundingBorder>
  );
};

export default ReturnByFilePage;
